/**
 * TaskAttachments component - displays and manages task file attachments
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { clsx } from 'clsx';
import {
  Paperclip,
  Upload,
  File,
  Image,
  FileText,
  Film,
  Music,
  Archive,
  Trash2,
  Download,
  Loader2,
  Eye,
} from 'lucide-react';
import { formatDistanceToNow } from '@/i18n/dateFns';
import { commentsApi, type Attachment } from '@services/api';
import { useAuthStore } from '@stores/authStore';
import { toast } from '@components/ui/Toast';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import AttachmentViewer from './AttachmentViewer';

interface TaskAttachmentsProps {
  taskId: string;
}

// File type icons mapping
const getFileIcon = (contentType: string | undefined) => {
  if (!contentType) return File;
  if (contentType.startsWith('image/')) return Image;
  if (contentType.startsWith('video/')) return Film;
  if (contentType.startsWith('audio/')) return Music;
  if (contentType.includes('pdf') || contentType.includes('document') || contentType.includes('text'))
    return FileText;
  if (contentType.includes('zip') || contentType.includes('archive') || contentType.includes('compressed'))
    return Archive;
  return File;
};

// Format file size
const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
};

export default function TaskAttachments({ taskId }: TaskAttachmentsProps) {
  const { t } = useTranslation();
  const [attachments, setAttachments] = useState<Attachment[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<Record<string, number>>({});
  const [isDragging, setIsDragging] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deletingAttachment, setDeletingAttachment] = useState<{ id: string; filename: string } | null>(null);
  const [viewingAttachment, setViewingAttachment] = useState<Attachment | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const dropZoneRef = useRef<HTMLDivElement>(null);

  const loadAttachments = useCallback(async () => {
    try {
      setIsLoading(true);
      const data = await commentsApi.getTaskAttachments(taskId);
      setAttachments(data);
    } catch (error) {
      console.error('Failed to load attachments:', error);
      toast.error(t('task.failedLoadAttachments'));
    } finally {
      setIsLoading(false);
    }
  }, [taskId, t]);

  useEffect(() => {
    void loadAttachments();
  }, [loadAttachments]);

  const uploadFile = useCallback(async (file: File) => {
    const tempId = `temp-${Date.now()}-${file.name}`;

    try {
      // Update progress state
      setUploadProgress(prev => ({ ...prev, [tempId]: 0 }));

      // 1. Initiate upload to get presigned URL
      const { attachmentId, uploadUrl } = await commentsApi.initiateTaskAttachmentUpload(taskId, {
        filename: file.name,
        contentType: file.type || 'application/octet-stream',
        sizeBytes: file.size,
      });

      setUploadProgress(prev => ({ ...prev, [tempId]: 20 }));

      // 2. Upload file through API proxy (get token from auth store)
      const { accessToken } = useAuthStore.getState();
      const uploadHeaders: HeadersInit = {
        'Content-Type': file.type || 'application/octet-stream',
      };
      if (accessToken) {
        uploadHeaders['Authorization'] = `Bearer ${accessToken}`;
      }
      const uploadResponse = await fetch(uploadUrl, {
        method: 'PUT',
        body: file,
        headers: uploadHeaders,
      });

      if (!uploadResponse.ok) {
        throw new Error('Upload failed');
      }

      setUploadProgress(prev => ({ ...prev, [tempId]: 80 }));

      // 3. Confirm upload
      const attachment = await commentsApi.confirmAttachmentUpload(attachmentId);

      setUploadProgress(prev => ({ ...prev, [tempId]: 100 }));

      // Add to attachments list
      setAttachments(prev => [attachment, ...prev]);

      // Clean up progress
      setTimeout(() => {
        setUploadProgress(prev => {
          const newProgress = { ...prev };
          delete newProgress[tempId];
          return newProgress;
        });
      }, 500);

      toast.success(t('common.uploadedFile', { filename: file.name }));
    } catch (error: unknown) {
      console.error('Failed to upload file:', error);
      const message = error instanceof Error ? error.message :
        (typeof error === 'object' && error !== null && 'message' in error) ? String((error as {message: unknown}).message) :
        'Upload failed';
      toast.error(t('common.uploadFailed', { filename: file.name, message }));

      // Clean up progress on error
      setUploadProgress(prev => {
        const newProgress = { ...prev };
        delete newProgress[tempId];
        return newProgress;
      });
    }
  }, [taskId, t]);

  const handleFileSelect = useCallback(async (files: FileList | null) => {
    if (!files || files.length === 0) return;

    setIsUploading(true);

    // Upload files sequentially to avoid overwhelming the server
    for (const file of Array.from(files)) {
      await uploadFile(file);
    }

    setIsUploading(false);

    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, [uploadFile]);

  const handleDelete = (attachmentId: string, filename: string) => {
    setDeletingAttachment({ id: attachmentId, filename });
    setShowDeleteConfirm(true);
  };

  const handleConfirmDelete = async () => {
    if (!deletingAttachment) return;

    try {
      await commentsApi.deleteAttachment(deletingAttachment.id);
      setAttachments(prev => prev.filter(a => a.id !== deletingAttachment.id));
      toast.success(t('task.attachmentDeleted'));
    } catch (error) {
      console.error('Failed to delete attachment:', error);
      toast.error(t('task.failedDeleteAttachment'));
    } finally {
      setShowDeleteConfirm(false);
      setDeletingAttachment(null);
    }
  };

  const handleDownload = async (attachment: Attachment) => {
    try {
      // Get fresh download URL
      const { url } = await commentsApi.getAttachment(attachment.id);
      if (url) {
        window.open(url, '_blank');
      }
    } catch (error) {
      console.error('Failed to download attachment:', error);
      toast.error(t('task.failedDownloadAttachment'));
    }
  };

  const handleView = (attachment: Attachment) => {
    setViewingAttachment(attachment);
  };

  const getAttachmentUrl = useCallback(async (id: string): Promise<string> => {
    const { url } = await commentsApi.getAttachment(id);
    return url || '';
  }, []);

  // Drag and drop handlers
  const handleDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    // Only set dragging to false if we're leaving the drop zone entirely
    if (dropZoneRef.current && !dropZoneRef.current.contains(e.relatedTarget as Node)) {
      setIsDragging(false);
    }
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const files = e.dataTransfer.files;
    void handleFileSelect(files);
  }, [handleFileSelect]);

  const activeUploads = Object.entries(uploadProgress);

  return (
    <div>
      <div className="mb-3 flex items-center justify-between">
        <h3 className="flex items-center gap-2 text-sm font-medium text-neutral-500 dark:text-neutral-400">
          <Paperclip className="h-4 w-4" />
          {t('task.attachments')}
          {attachments.length > 0 && (
            <span className="text-neutral-400">({attachments.length})</span>
          )}
        </h3>
        <button
          onClick={() => fileInputRef.current?.click()}
          disabled={isUploading}
          className={clsx(
            'flex items-center gap-1 rounded px-2 py-1 text-xs',
            'text-neutral-600 hover:bg-neutral-100',
            'dark:text-neutral-400 dark:hover:bg-neutral-800',
            'disabled:cursor-not-allowed disabled:opacity-50'
          )}
        >
          <Upload className="h-3 w-3" />
          {t('task.uploadLabel')}
        </button>
      </div>

      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        multiple
        className="hidden"
        onChange={e => void handleFileSelect(e.target.files)}
      />

      {/* Drop zone */}
      <div
        ref={dropZoneRef}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        className={clsx(
          'rounded-lg border border-dashed transition-colors',
          isDragging
            ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
            : 'border-neutral-300 dark:border-neutral-700',
          'p-3'
        )}
      >
        {isDragging ? (
          <div className="flex flex-col items-center justify-center py-4 text-primary-600 dark:text-primary-400">
            <Upload className="mb-2 h-8 w-8" />
            <span className="text-sm font-medium">{t('task.dropFilesHere')}</span>
          </div>
        ) : isLoading ? (
          <div className="py-4 text-center text-sm text-neutral-500">
            {t('task.loadingAttachments')}
          </div>
        ) : (
          <>
            {/* Active uploads */}
            {activeUploads.length > 0 && (
              <div className="mb-3 space-y-2">
                {activeUploads.map(([id, progress]) => (
                  <div key={id} className="flex items-center gap-2">
                    <Loader2 className="h-4 w-4 animate-spin text-primary-600" />
                    <div className="flex-1">
                      <div className="h-1.5 overflow-hidden rounded-full bg-neutral-200 dark:bg-neutral-700">
                        <div
                          className="h-full rounded-full bg-primary-600 transition-all duration-300"
                          style={{ width: `${progress}%` }}
                        />
                      </div>
                    </div>
                    <span className="text-xs text-neutral-500">{progress}%</span>
                  </div>
                ))}
              </div>
            )}

            {/* Attachments list */}
            {attachments.length > 0 ? (
              <div className="space-y-2">
                {attachments.map(attachment => (
                  <AttachmentItem
                    key={attachment.id}
                    attachment={attachment}
                    onView={() => handleView(attachment)}
                    onDownload={() => handleDownload(attachment)}
                    onDelete={() => handleDelete(attachment.id, attachment.filename)}
                  />
                ))}
              </div>
            ) : (
              <button
                onClick={() => fileInputRef.current?.click()}
                className={clsx(
                  'flex w-full flex-wrap items-center gap-x-3 gap-y-1 text-left',
                  'text-neutral-500 hover:text-neutral-600',
                  'dark:text-neutral-400 dark:hover:text-neutral-300'
                )}
              >
                <Paperclip className="h-4 w-4 shrink-0" />
                <span className="text-sm">{t('task.dropOrClickUpload')}</span>
                <span className="w-full pl-7 text-xs text-neutral-500">
                  {t('task.maxFileSize')}
                </span>
              </button>
            )}
          </>
        )}
      </div>

      <ConfirmDialog
        open={showDeleteConfirm}
        onClose={() => {
          setShowDeleteConfirm(false);
          setDeletingAttachment(null);
        }}
        onConfirm={handleConfirmDelete}
        title={t('task.deleteAttachmentTitle')}
        description={t('task.deleteAttachmentDescription', { filename: deletingAttachment?.filename ?? '' })}
        confirmLabel={t('common.delete')}
        variant="danger"
      />

      <AttachmentViewer
        attachment={viewingAttachment}
        attachments={attachments}
        isOpen={viewingAttachment !== null}
        onClose={() => setViewingAttachment(null)}
        onNavigate={setViewingAttachment}
        getAttachmentUrl={getAttachmentUrl}
      />
    </div>
  );
}

interface AttachmentItemProps {
  attachment: Attachment;
  onView: () => void;
  onDownload: () => void;
  onDelete: () => void;
}

function AttachmentItem({ attachment, onView, onDownload, onDelete }: AttachmentItemProps) {
  const { t } = useTranslation();
  const FileIcon = getFileIcon(attachment.contentType);
  const isImage = attachment.contentType?.startsWith('image/') ?? false;
  const timeAgo = attachment.createdAt
    ? formatDistanceToNow(new Date(attachment.createdAt), { addSuffix: true })
    : 'just now';

  return (
    <div
      className={clsx(
        'group flex items-center gap-3 rounded-lg p-2',
        'hover:bg-neutral-100 dark:hover:bg-neutral-800/50'
      )}
    >
      {/* Thumbnail or icon */}
      {isImage && attachment.thumbnailUrl ? (
        <img
          src={attachment.thumbnailUrl}
          alt={attachment.filename}
          className="h-10 w-10 rounded object-cover"
        />
      ) : (
        <div className="flex h-10 w-10 items-center justify-center rounded bg-neutral-100 dark:bg-neutral-800">
          <FileIcon className="h-5 w-5 text-neutral-500" />
        </div>
      )}

      {/* Info */}
      <div className="flex-1 min-w-0">
        <p className="truncate text-sm font-medium text-neutral-900 dark:text-white">
          {attachment.filename}
        </p>
        <p className="text-xs text-neutral-500">
          {formatFileSize(attachment.sizeBytes)} · {timeAgo}
        </p>
      </div>

      {/* Actions */}
      <div className="flex items-center gap-1 opacity-0 transition-opacity group-hover:opacity-100">
        <button
          onClick={onView}
          className="rounded p-1.5 text-neutral-500 hover:bg-primary-100 hover:text-primary-600 dark:hover:bg-primary-900/30"
          title={t('common.view')}
        >
          <Eye className="h-4 w-4" />
        </button>
        <button
          onClick={onDownload}
          className="rounded p-1.5 text-neutral-500 hover:bg-neutral-200 dark:hover:bg-neutral-700"
          title={t('common.download')}
        >
          <Download className="h-4 w-4" />
        </button>
        <button
          onClick={onDelete}
          className="rounded p-1.5 text-neutral-500 hover:bg-red-100 hover:text-red-600 dark:hover:bg-red-900/30"
          title={t('common.delete')}
        >
          <Trash2 className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}
